import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useOnboarding, useForwardAction } from '@/components/onboarding'
import {
  ATTESTATION_ITEMS,
  BAA_TEXT,
  TSA_TEXT,
  type AttestationItem,
} from '@/data/onboarding-agreements'
import type { AgreementsStepData } from '@/types/onboarding'

const STEP_INDEX = 2

const requiredTrue = z
  .boolean()
  .refine((v) => v === true, { message: 'Required' })

const agreementsFormSchema = z.object({
  tsa_agreed: requiredTrue,
  baa_agreed: requiredTrue,
  attest_malpractice: requiredTrue,
  attest_scope: requiredTrue,
  attest_hipaa: requiredTrue,
  attest_telehealth: requiredTrue,
  legal_name: z
    .string()
    .refine((v) => v.trim().length > 0, { message: 'Legal name is required' }),
})

type AgreementsFormValues = z.infer<typeof agreementsFormSchema>

const agreementsStepDataSchema = agreementsFormSchema.extend({
  signed_at: z.string().min(1),
})

function getStoredData(stepData: Record<string, unknown>): AgreementsStepData | null {
  const stored = stepData[STEP_INDEX]
  if (!stored) return null
  const parsed = agreementsStepDataSchema.safeParse(stored)
  return parsed.success ? parsed.data : null
}

function AgreementCard({
  title,
  text,
  checkboxId,
  label,
  checked,
  onCheckedChange,
  error,
}: {
  title: string
  text: string
  checkboxId: string
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  error?: string
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ScrollArea className="h-64 rounded border p-4">
          <div className="whitespace-pre-wrap text-sm text-slate-600">{text}</div>
        </ScrollArea>
        <div className="flex items-center gap-2">
          <Checkbox
            id={checkboxId}
            checked={checked}
            onCheckedChange={(val) => onCheckedChange(val === true)}
          />
          <Label htmlFor={checkboxId} className="text-sm leading-none">
            {label}
          </Label>
        </div>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function AttestationRow({
  item,
  checked,
  onCheckedChange,
  error,
}: {
  item: AttestationItem
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  error?: string
}) {
  return (
    <div className="space-y-1">
      {/* Center the checkbox against the label's single line; the description
          sits below on its own indented line so the checkbox never drifts
          against multi-line text. */}
      <div className="flex items-center gap-2">
        <Checkbox
          id={item.id}
          checked={checked}
          onCheckedChange={(val) => onCheckedChange(val === true)}
        />
        <Label htmlFor={item.id} className="text-sm font-medium leading-none">
          {item.label}
        </Label>
      </div>
      <p className="ml-6 text-sm text-slate-600">{item.description}</p>
      {error && (
        <p role="alert" className="ml-6 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}

export function AgreementsStep() {
  const { state, setStepData, markCompleted, goToStep } = useOnboarding()

  // Seed form defaults exactly once from state at mount — see rationale in
  // CredentialsStep.
  const [defaults] = useState<AgreementsFormValues>(() => {
    const stored = getStoredData(state.stepData)
    if (stored) {
      return {
        tsa_agreed: stored.tsa_agreed,
        baa_agreed: stored.baa_agreed,
        attest_malpractice: stored.attest_malpractice,
        attest_scope: stored.attest_scope,
        attest_hipaa: stored.attest_hipaa,
        attest_telehealth: stored.attest_telehealth,
        legal_name: stored.legal_name,
      }
    }
    return {
      tsa_agreed: false,
      baa_agreed: false,
      attest_malpractice: false,
      attest_scope: false,
      attest_hipaa: false,
      attest_telehealth: false,
      legal_name: '',
    }
  })

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<AgreementsFormValues>({
    resolver: zodResolver(agreementsFormSchema),
    mode: 'onChange',
    defaultValues: defaults,
  })

  const values = watch()

  const onSubmit = (data: AgreementsFormValues) => {
    // TODO(#693 follow-up): when the backend onboarding endpoint lands, it
    // should stamp signed_at server-side from request arrival time. A
    // client-generated timestamp is trivially spoofable and must not be
    // treated as the legally binding signature time.
    const payload: AgreementsStepData = {
      ...data,
      signed_at: new Date().toISOString(),
    }
    setStepData(STEP_INDEX, payload)
    markCompleted(STEP_INDEX)
    goToStep(STEP_INDEX + 1)
  }

  useForwardAction({
    label: 'Sign & continue',
    disabled: !isValid,
    onClick: handleSubmit(onSubmit),
  })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-slate-900">Agreements & attestations</h2>
        <p className="mt-1 text-sm text-slate-600">
          Please review the agreements and confirm the attestations below. Your typed
          name serves as your signature.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
        aria-label="Agreements form"
      >
        <AgreementCard
          title="Technology Services Agreement"
          text={TSA_TEXT}
          checkboxId="tsa_agreed"
          label="I have read and agree to the Technology Services Agreement."
          checked={values.tsa_agreed}
          onCheckedChange={(checked) =>
            setValue('tsa_agreed', checked, { shouldValidate: true })
          }
          error={errors.tsa_agreed?.message}
        />

        <AgreementCard
          title="Business Associate Agreement"
          text={BAA_TEXT}
          checkboxId="baa_agreed"
          label="I have read and agree to the Business Associate Agreement."
          checked={values.baa_agreed}
          onCheckedChange={(checked) =>
            setValue('baa_agreed', checked, { shouldValidate: true })
          }
          error={errors.baa_agreed?.message}
        />

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Attestations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {ATTESTATION_ITEMS.map((item) => (
              <AttestationRow
                key={item.id}
                item={item}
                checked={values[item.id]}
                onCheckedChange={(checked) =>
                  setValue(item.id, checked, { shouldValidate: true })
                }
                error={errors[item.id]?.message}
              />
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Signature</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Label htmlFor="legal_name">Full legal name</Label>
            <Input
              id="legal_name"
              placeholder="Enter your full legal name"
              {...register('legal_name')}
            />
            {errors.legal_name && (
              <p role="alert" className="text-sm text-red-600">
                {errors.legal_name.message}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Visually hidden submit so pressing Enter in the name field triggers
            handleSubmit. Distinct accessible name from the wizard's
            forward-action button ("Sign & continue") so test queries stay
            unambiguous. */}
        <button type="submit" aria-label="Submit agreements form" className="sr-only" tabIndex={-1} />
      </form>
    </div>
  )
}
